import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardContent, Input, Select } from '@components';
import { useToast } from '../../../contexts/useToast';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { FaqEntryModal } from '../components/FaqEntryModal';
import {
  unansweredQuestionKeys,
  useAnswerUnansweredQuestion,
  useDismissUnansweredQuestion,
  useResolveUnansweredQuestion,
  useUnansweredQuestionDetail,
  useUnansweredQuestions,
  useUpdateFaqEntry,
} from '../hooks';
import { QuestionAlreadyHandledError } from '../services/unansweredQuestionsService';
import {
  FaqEntryInput,
  KnowledgeBase,
  UnansweredQuestion,
  UnansweredQuestionStatus,
} from '../types';

const SEARCH_DEBOUNCE_MS = 300;

const STATUSES: UnansweredQuestionStatus[] = ['open', 'resolved_with_faq', 'resolved', 'dismissed'];

const STATUS_BADGES: Record<UnansweredQuestionStatus, 'warning' | 'success' | 'secondary'> = {
  open: 'warning',
  resolved_with_faq: 'success',
  resolved: 'success',
  dismissed: 'secondary',
};

interface UnansweredQuestionsTabProps {
  knowledgeBase: KnowledgeBase;
}

export const UnansweredQuestionsTab: React.FC<UnansweredQuestionsTabProps> = ({
  knowledgeBase,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<UnansweredQuestionStatus>('open');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [answering, setAnswering] = useState<UnansweredQuestion | null>(null);
  // Dismissing is final, so it is confirmed first
  const [dismissing, setDismissing] = useState<UnansweredQuestion | null>(null);
  // The FAQ entry of a question that came up again although it was answered
  const [editingFaqOf, setEditingFaqOf] = useState<UnansweredQuestion | null>(null);

  const {
    data: questions = [],
    isLoading,
    isError,
  } = useUnansweredQuestions(knowledgeBase.id, status, { search });

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);
  const answerQuestion = useAnswerUnansweredQuestion();
  const resolveQuestion = useResolveUnansweredQuestion();
  const dismissQuestion = useDismissUnansweredQuestion();
  const updateFaqEntry = useUpdateFaqEntry();

  const handleAnswer = async (data: FaqEntryInput) => {
    if (!answering) return;
    await answerQuestion.mutateAsync({ id: answering.id, data });
    showToast({ type: 'success', message: t('knowledgeBase.unanswered.answered') });
  };

  const handleEditFaq = async (data: FaqEntryInput) => {
    const faqEntry = editingFaqOf?.resolved_faq_entry;
    if (!faqEntry) return;
    await updateFaqEntry.mutateAsync({ id: faqEntry.id, data });
    queryClient.invalidateQueries({ queryKey: unansweredQuestionKeys.all });
  };

  const handleDismiss = async () => {
    if (!dismissing) return;
    await handleAction(dismissing, 'dismiss');
    setDismissing(null);
  };

  const handleAction = async (question: UnansweredQuestion, action: 'resolve' | 'dismiss') => {
    const mutation = action === 'resolve' ? resolveQuestion : dismissQuestion;
    try {
      await mutation.mutateAsync(question.id);
      showToast({
        type: 'success',
        message: t(
          action === 'resolve'
            ? 'knowledgeBase.unanswered.resolvedToast'
            : 'knowledgeBase.unanswered.dismissedToast'
        ),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof QuestionAlreadyHandledError
            ? err.message
            : t('knowledgeBase.unanswered.actionFailed'),
      });
    }
  };

  const busyId =
    (resolveQuestion.isPending && resolveQuestion.variables) ||
    (dismissQuestion.isPending && dismissQuestion.variables) ||
    null;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('knowledgeBase.unanswered.title')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('knowledgeBase.unanswered.description')}
        </p>
      </div>

      {!knowledgeBase.unansweredQuestionsEnabled && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <span>{t('knowledgeBase.unanswered.disabledHint')}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/knowledge-base/${knowledgeBase.id}/settings`)}
            className="shrink-0"
          >
            {t('knowledgeBase.tabs.settings')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Input
          type="text"
          placeholder={t('knowledgeBase.unanswered.search')}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="w-80 h-10"
        />
        <Select
          value={status}
          onChange={value =>
            setStatus((Array.isArray(value) ? value[0] : value) as UnansweredQuestionStatus)
          }
          options={STATUSES.map(value => ({
            value,
            label: t(`knowledgeBase.unanswered.status.${value}`),
          }))}
          className="w-60 h-10"
        />
      </div>

      <Card>
        <CardContent>
          {isError ? (
            <p className="py-8 text-center text-red-500">
              {t('knowledgeBase.unanswered.loadFailed')}
            </p>
          ) : isLoading ? (
            <p className="py-8 text-center text-gray-500">{t('common.loading')}</p>
          ) : questions.length === 0 ? (
            <div className="py-12 text-center space-y-2">
              <span className="material-icons text-3xl text-gray-400">help_outline</span>
              <p className="text-gray-600 dark:text-gray-400">
                {search
                  ? t('knowledgeBase.unanswered.noResults')
                  : status === 'open'
                    ? t('knowledgeBase.unanswered.emptyOpen')
                    : t('knowledgeBase.unanswered.empty')}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {questions.map(question => (
                <UnansweredQuestionRow
                  key={question.id}
                  question={question}
                  onAnswer={() => setAnswering(question)}
                  onEditFaq={() => setEditingFaqOf(question)}
                  onResolve={() => handleAction(question, 'resolve')}
                  onDismiss={() => setDismissing(question)}
                  isBusy={busyId === question.id}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <FaqEntryModal
        isOpen={answering !== null}
        onClose={() => setAnswering(null)}
        onSubmit={handleAnswer}
        entry={null}
        initialQuestion={answering?.question ?? ''}
        title={t('knowledgeBase.unanswered.answerAsFaq')}
        submitLabel={t('knowledgeBase.unanswered.saveAsFaq')}
        isSaving={answerQuestion.isPending}
      />

      <FaqEntryModal
        isOpen={editingFaqOf !== null}
        onClose={() => setEditingFaqOf(null)}
        onSubmit={handleEditFaq}
        entry={editingFaqOf?.resolved_faq_entry ?? null}
        isSaving={updateFaqEntry.isPending}
      />

      <ConfirmDeleteModal
        isOpen={dismissing !== null}
        onClose={() => setDismissing(null)}
        onConfirm={handleDismiss}
        title={t('knowledgeBase.unanswered.dismissTitle')}
        question={t('knowledgeBase.unanswered.dismissQuestion')}
        itemName={dismissing?.question ?? ''}
        warning={t('knowledgeBase.unanswered.dismissWarning')}
        confirmLabel={t('knowledgeBase.unanswered.dismiss')}
        icon="block"
        isDeleting={dismissQuestion.isPending}
      />
    </div>
  );
};

interface UnansweredQuestionRowProps {
  question: UnansweredQuestion;
  onAnswer: () => void;
  onEditFaq: () => void;
  onResolve: () => void;
  onDismiss: () => void;
  isBusy: boolean;
}

const UnansweredQuestionRow: React.FC<UnansweredQuestionRowProps> = ({
  question,
  onAnswer,
  onEditFaq,
  onResolve,
  onDismiss,
  isBusy,
}) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isOpen = question.status === 'open';

  return (
    <li className="py-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-gray-900 dark:text-white break-words">
            {question.question}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('knowledgeBase.unanswered.askedCount', { count: question.occurrence_count })}
            {' · '}
            {t('knowledgeBase.unanswered.lastAsked', {
              date: new Date(question.last_seen_at).toLocaleDateString(i18n.language),
            })}
          </p>
        </div>
        {isOpen && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button variant="gradient-multi" size="sm" onClick={onAnswer} disabled={isBusy}>
              {t('knowledgeBase.unanswered.answerAsFaq')}
            </Button>
            <Button variant="outline" size="sm" onClick={onResolve} disabled={isBusy}>
              {t('knowledgeBase.unanswered.resolve')}
            </Button>
            <Button variant="ghost" size="sm" onClick={onDismiss} disabled={isBusy}>
              {t('knowledgeBase.unanswered.dismiss')}
            </Button>
          </div>
        )}
      </div>

      {question.resolved_faq_entry && (
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-800">
          <div className="min-w-0">
            <p className="text-gray-600 dark:text-gray-400">
              {isOpen
                ? t('knowledgeBase.unanswered.askedAgainDespiteFaq')
                : t('knowledgeBase.unanswered.answeredWithFaq')}
            </p>
            <p className="font-medium text-gray-900 dark:text-white break-words">
              {question.resolved_faq_entry.question}
            </p>
            <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line break-words line-clamp-3">
              {question.resolved_faq_entry.answer}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onEditFaq} className="shrink-0">
            {t('knowledgeBase.faq.editEntry')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={STATUS_BADGES[question.status]} size="sm">
          {t(`knowledgeBase.unanswered.status.${question.status}`)}
        </Badge>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          <span className="material-icons text-base">
            {isExpanded ? 'expand_less' : 'expand_more'}
          </span>
          {isExpanded
            ? t('knowledgeBase.unanswered.hideCalls')
            : t('knowledgeBase.unanswered.showCalls')}
        </button>
      </div>

      {isExpanded && <Occurrences questionId={question.id} />}
    </li>
  );
};

const Occurrences: React.FC<{ questionId: string }> = ({ questionId }) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useUnansweredQuestionDetail(questionId, true);

  if (isError) {
    return <p className="text-sm text-red-500">{t('knowledgeBase.unanswered.loadFailed')}</p>;
  }
  if (isLoading || !data) {
    return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  }
  if (data.occurrences.length === 0) {
    // Excerpts are deleted together with their calls
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('knowledgeBase.unanswered.noCalls')}
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {data.occurrences.map(occurrence => (
        <li
          key={occurrence.id}
          className="rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-gray-500 dark:text-gray-400">
              {occurrence.conversation_started_at
                ? new Date(occurrence.conversation_started_at).toLocaleString(i18n.language)
                : ''}
              {occurrence.caller_number && ` · ${occurrence.caller_number}`}
            </p>
            {occurrence.call && (
              <Link
                to={`/calls?call=${occurrence.call}`}
                className="flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                <span className="material-icons text-base">open_in_new</span>
                {t('knowledgeBase.unanswered.openCall')}
              </Link>
            )}
          </div>
          <p className="mt-2 whitespace-pre-line break-words text-gray-700 dark:text-gray-300">
            {occurrence.context_excerpt || occurrence.original_question}
          </p>
        </li>
      ))}
    </ul>
  );
};
